import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "title", "searchInput", "valuesList", "addNewSection"
  ]
  static values = {
    selectedValue: String,
    invoiceId: String,
    vatLineId: String,
    lookups: Array,
    lookupType: String,
    syncedTypes: Array
  }

  connect() {
    // Listen for lookup creation events from the shared modal
    this.handleLookupCreated = this.handleLookupCreated.bind(this)
    document.addEventListener('lookup-created', this.handleLookupCreated)
  }

  disconnect() {
    document.removeEventListener('lookup-created', this.handleLookupCreated)
  }

  handleLookupCreated(event) {
    // Only process if this controller has been opened (has a modalId)
    if (!this.modalId) return

    const { lookup, lookupType, callerId } = event.detail

    // Only handle events meant for this modal instance
    if (callerId !== this.modalId) return

    // Add the new lookup to our list
    this.lookupsValue = [...this.lookupsValue, lookup]

    // Select the new lookup
    this.selectedLookupId = lookup.id
    this.selectedValueValue = lookup.kodas
    this.searchInputTarget.value = lookup.kodas

    // Save the lookup to the invoice/vat_line
    this.saveLookup()
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()

    // Get data from clicked element
    const button = event.currentTarget
    const perspective = button.dataset.perspective

    // Check if perspective is available from data attribute
    if (!perspective) return

    // Store perspective for saving
    this.perspective = perspective

    // Generate unique modal ID for this instance
    this.modalId = `simple-edit-${Date.now()}`

    const title = button.dataset.modalTitle
    const fieldName = button.dataset.modalFieldName
    const currentValue = button.dataset.modalContent
    const invoiceId = button.dataset.invoiceId
    const vatLineId = button.dataset.vatLineId

    // fieldName is the lookup type directly (e.g., 'preke', 'sandelis')
    const knownLookupTypes = ['preke', 'padalinys', 'objektas', 'serija', 'centras', 'atskaitingas_asmuo', 'sandelis', 'korespondencija', 'projektas']
    this.lookupTypeValue = knownLookupTypes.includes(fieldName) ? fieldName : ''

    // Update modal title
    this.titleTarget.textContent = title

    // Update UI
    this.searchInputTarget.value = currentValue

    // Store values for saving
    this.selectedValueValue = currentValue
    this.invoiceIdValue = invoiceId
    this.vatLineIdValue = vatLineId

    // Load lookups
    if (this.lookupTypeValue) {
      this.loadLookups(this.lookupTypeValue)
    } else {
      this.lookupsValue = []
    }

    // Show modal and update values list
    this._showModal()
    this.updateValuesList()
  }

  _showModal() {
    this.modalTarget.showModal()
  }

  close() {
    this.modalTarget.close()
  }

  closed() {
    this.searchInputTarget.value = ''
    this.selectedValueValue = ''
    this.modalId = null
    this.selectedLookupId = null
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.close()
  }

  search(event) {
    const searchTerm = event.target.value.toLowerCase()
    this.updateValuesList(searchTerm)
  }

  async loadLookups(lookupType) {
    try {
      const response = await fetch(`/accounting_lookups.json?lookup_type=${lookupType}`)
      if (response.ok) {
        const data = await response.json()
        this.lookupsValue = data.lookups || []
        this.updateValuesList()
      }
    } catch (error) {
      console.error('Error loading lookups:', error)
      this.lookupsValue = []
    }
  }

  updateValuesList(searchTerm = '') {
    // Clear current list
    this.valuesListTarget.innerHTML = ''

    if (this.lookupsValue.length > 0) {
      const filteredLookups = this.lookupsValue.filter(lookup => {
        const kodas = (lookup.kodas || '').toLowerCase()
        const pavadinimas = (lookup.pavadinimas || '').toLowerCase()
        return kodas.includes(searchTerm.toLowerCase()) || pavadinimas.includes(searchTerm.toLowerCase())
      })

      filteredLookups.forEach(lookup => {
        const button = this.createLookupButton(lookup)
        this.valuesListTarget.appendChild(button)
      })
    }

    // Show "Sukurti naują" unless type is synced
    const isSynced = this.syncedTypesValue.includes(this.lookupTypeValue)
    this.addNewSectionTarget.classList.toggle('hidden', isSynced)
  }

  createLookupButton(lookup) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-md flex items-center justify-between'
    button.dataset.action = 'click->simple-edit-modal#selectLookup'
    button.dataset.lookupId = lookup.id
    button.dataset.kodas = lookup.kodas
    button.dataset.pavadinimas = lookup.pavadinimas || ''

    let displayHtml = `<div class="flex flex-col min-w-0"><span class="font-medium truncate">${this.escapeHtml(lookup.kodas)}</span>`
    if (lookup.pavadinimas) {
      displayHtml += `<span class="text-xs text-gray-500 truncate">${this.escapeHtml(lookup.pavadinimas)}</span>`
    }
    displayHtml += '</div>'
    button.innerHTML = displayHtml

    return button
  }

  selectLookup(event) {
    const lookupId = event.currentTarget.dataset.lookupId
    const kodas = event.currentTarget.dataset.kodas
    this.selectedValueValue = kodas
    this.selectedLookupId = lookupId
    this.searchInputTarget.value = kodas
    this.saveLookup()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  addNewValue(event) {
    event.preventDefault()
    this.openCreateModal()
  }

  openCreateModal() {
    // Dispatch event to open the shared lookup create modal
    document.dispatchEvent(new CustomEvent('open-lookup-create-modal', {
      detail: {
        lookupType: this.lookupTypeValue,
        prefillKodas: this.searchInputTarget.value.trim(),
        callerId: this.modalId
      }
    }))
  }

  async saveLookup() {
    if (!this.selectedLookupId) return

    // Use stored perspective (set in open method)
    const perspective = this.perspective

    try {
      const url = this.vatLineIdValue
        ? `/invoices/${this.invoiceIdValue}/vat_lines/${this.vatLineIdValue}/update_lookup`
        : `/invoices/${this.invoiceIdValue}/update_lookup`

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify({
          lookup_type: this.lookupTypeValue,
          lookup_id: this.selectedLookupId,
          perspective: perspective
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to save lookup: ${response.status}`)
      }

      // Process Turbo Stream response
      const html = await response.text()
      Turbo.renderStreamMessage(html)

      this.close()
    } catch (error) {
      console.error('Error saving lookup:', error)
    }
  }

} 